import { useState } from "react";

import {
  LogsQueryClient,
  LogsQueryResultStatus,
  QueryTimeInterval,
} from "@azure/monitor-query-logs";
import config from "./Config.ts";
import { App, extractApp, resourceGroup } from "./applications.ts";
import { useAuth } from "./AuthContext.tsx";

export interface FetchState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  fetchData: () => void;
}

export const useQueryApplication = (
  query: LogsQuery
): FetchState<Logline[]> => {
  const { credential } = useAuth();
  const [data, setData] = useState<Logline[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  function fetchData() {
    setLoading(true);
    const logsQueryClient = new LogsQueryClient(credential);
    const result = logsQueryClient.queryWorkspace(
      config.logWorkspaceId,
      query.query,
      query.timeInterval
    );

    result
      .then((value) => {
        if (value.status === LogsQueryResultStatus.Success) {
          const tablesFromResult = value.tables;
          const idx = tablesFromResult[0].columnDescriptors
            .map((c) => c.name)
            .indexOf("ResultDescription");
          const idxApplication = tablesFromResult[0].columnDescriptors
            .map((c) => c.name)
            .indexOf("_ResourceId");

          const seenIds = new Set<string>();
          const lines = tablesFromResult[0].rows
            .map((l) => [l[idxApplication], l[idx]])
            .map((row) => {
              const [r, l] = row;
              const l1 = l as string;
              const app = extractApp(r as string);
              const id = djb2Hash(app + l1);
              return new Logline(id, app, l1);
            })
            .filter((line) => {
              // It seems there are duplicates
              if (seenIds.has(line.id)) {
                return false;
              } else {
                seenIds.add(line.id);
                return true;
              }
            });

          setData(lines);
        } else {
          console.log(
            `Error processing the query '${query}' - ${value.partialError}`
          );
          if (value.partialTables.length > 0) {
            console.log(
              `This query has also returned partial data in the following table(s) - ${value.partialTables}`
            );
          }
        }
      })
      .catch((error) => setError(error as Error))
      .finally(() => setLoading(false));
  }

  return { data, loading, error, fetchData };
};

export class LogsQuery {
  readonly query: string;
  readonly timeInterval: QueryTimeInterval;

  constructor(query: string, timeInterval: QueryTimeInterval) {
    this.query = query;
    this.timeInterval = timeInterval;
  }
}

function getResourceIdInLog(application: App) {
  const { name, slot } = application;
  const slotPath = slot == "staging" ? `/slots/${slot}` : "";
  return `/subscriptions/${
    config.subscriptionId
  }/resourcegroups/${resourceGroup(
    name
  )}/providers/microsoft.web/sites/${resourceGroup(name)}-app${slotPath}`;
}

export interface LogFilters {
  levels?: string[];
  traceId?: string;
}

// Escapes a value for use inside a double quoted KQL string literal
function escapeKql(value: string) {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

// A log line is either json, with the level quoted, or plain text with the
// level surrounded by spaces
function levelCondition(level: string) {
  return `ResultDescription has_cs " ${level} " or ResultDescription has_cs "\\"${level}\\""`;
}

// Without an application the query covers the logs of every application
export const applicationLogsQuery = function (
  application: App | undefined,
  timeInterval: QueryTimeInterval,
  filters: LogFilters = {}
) {
  const conditions: string[] = [];
  if (application) {
    conditions.push(`_ResourceId == "${getResourceIdInLog(application)}"`);
  }
  if (filters.traceId) {
    conditions.push(`ResultDescription has "${escapeKql(filters.traceId)}"`);
  }
  if (filters.levels && filters.levels.length > 0) {
    conditions.push(`(${filters.levels.map(levelCondition).join(" or ")})`);
  }
  const where =
    conditions.length > 0 ? ` | where ${conditions.join(" and ")}` : "";
  return new LogsQuery(
    `AppServiceConsoleLogs${where} | order by TimeGenerated desc`,
    timeInterval
  );
};

export class Logline {
  readonly id: string;
  readonly app: string;
  readonly line: string;

  constructor(id: string, app: string, line: string) {
    this.id = id;
    this.app = app;
    this.line = line;
  }
}
function djb2Hash(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return String(hash >>> 0); // Ensure the hash is a positive integer
}
